import { Component, type ReactNode, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell.js';
import { AuthRedirectError, fetchSession, logout, type SessionIdentity } from './api/client.js';
import { KeyedCourseBrowser } from './components/CourseBrowser.js';
import { EntityEditor } from './components/EntityEditor.js';
import { LessonTranscriptPage } from './components/LessonTranscriptPage.js';
import { StructuralDiffReview } from './components/StructuralDiffReview.js';
import { AuditLogPage } from './components/AuditLogPage.js';
import { ProgrammeDashboard } from './components/ProgrammeDashboard.js';
import { ReleaseWavesDashboard } from './components/ReleaseWavesDashboard.js';
import { WorkQueueBoard } from './components/WorkQueueBoard.js';
import { KeyedWorkQueueDashboard } from './components/WorkQueueDashboard.js';
import { KeyedMicroLessonReview } from './components/MicroLessonReview.js';
import { KeyedPracticeItemReview } from './components/PracticeItemReview.js';
import { sendSentryVerificationEvent } from './sentry.js';
import { startActivityHeartbeat } from './activity/signals.js';
import { readSessionValue, removeSessionValue } from './lib/browserStorage.js';

export function App() {
  const [status, setStatus] = useState<'loading' | 'ready' | 'signed_out' | 'error'>('loading');
  const [error, setError] = useState('');
  const [session, setSession] = useState<SessionIdentity | null>(null);

  useEffect(() => {
    let cancelled = false;
    /*
     * "DID THE REVIEWER DELIBERATELY SIGN OUT?", asked of two places because storage alone cannot
     * always answer it.
     *
     * This decides whether an unauthenticated load bounces straight to Google. Answer it wrongly in
     * the "no" direction and signing out does not stick: the redirect fires, Google still holds a
     * session, it is not asked to prompt, and the reviewer who clicked **Log out** lands back inside
     * the tool signed in. That is what `gpt-5.6-sol` found on 2026-08-29 once the storage write was
     * made non-throwing — the marker is not a convenience, it is the only thing distinguishing a
     * deliberate sign-out from an expired session.
     *
     * So the intent is carried BOTH in session storage and in the address `logout()` redirects to.
     * A browser that blocks site data silently drops the first; nothing can drop the second. Reading
     * either is enough, which is the safe direction: the cost of a false positive is one extra click
     * on a sign-in button, and the cost of a false negative is a sign-out that did not happen.
     */
    const signedOutMarker = readSessionValue('author.logged_out') === 'true';
    const signedOutInUrl =
      typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('signed_out') === '1';
    const loggedOut = signedOutMarker || signedOutInUrl;

    void fetchSession({ redirectOnUnauthorized: !loggedOut })
      .then((nextSession) => {
        if (!cancelled) {
          if (!nextSession) {
            setSession(null);
            setStatus('signed_out');
            return;
          }
          removeSessionValue('author.logged_out');
          setSession(nextSession);
          setStatus('ready');
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          if (reason instanceof AuthRedirectError) {
            return;
          }
          setError(reason instanceof Error ? reason.message : 'Unable to verify session');
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const marker = params.get('__sentry_test');
    if (!marker) {
      return;
    }
    void sendSentryVerificationEvent(marker).then((sent) => {
      window.__mindojoSentryVerification = { marker, sent };
    });
  }, []);

  // The interactive-presence heartbeat, started once a session exists and stopped when it ends.
  //
  // Gated on the session deliberately: before sign-in there is no subject to attribute presence to,
  // and the beacon would be rejected anyway. Started here rather than inside a review screen because
  // reading is not confined to one route — the reviewer also reads the queue, the wave dashboard and
  // the audit log, and a heartbeat that only ran on the item screen would report those as idle.
  const signedIn = status === 'ready' && session !== null;
  useEffect(() => {
    if (!signedIn) {
      return;
    }
    return startActivityHeartbeat();
  }, [signedIn]);

  if (status === 'loading') {
    return <div style={{ padding: 24 }}>Loading authoring workspace...</div>;
  }

  if (status === 'error') {
    return <div style={{ padding: 24 }}>{error}</div>;
  }

  if (status === 'signed_out') {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ margin: '0 0 12px' }}>Signed out.</p>
        <button
          onClick={() => {
            removeSessionValue('author.logged_out');
            window.location.assign('/auth/google');
          }}
          type="button"
        >
          Sign in again
        </button>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <Routes>
      <Route
        element={
          <AppShell
            onLogout={() => {
              void logout().then(() => {
                setSession(null);
                setStatus('signed_out');
              });
            }}
            session={session}
          />
        }
      >
        <Route element={<Navigate replace to="/courses" />} path="/" />
        <Route element={<RouteErrorBoundary><KeyedCourseBrowser /></RouteErrorBoundary>} path="/courses" />
        <Route element={<RouteErrorBoundary><EntityEditor /></RouteErrorBoundary>} path="/courses/:courseId/entities/:entityId" />
        <Route element={<RouteErrorBoundary><LessonTranscriptPage kind="entity" /></RouteErrorBoundary>} path="/courses/:courseId/entities/:entityId/transcript" />
        <Route element={<RouteErrorBoundary><LessonTranscriptPage kind="task" /></RouteErrorBoundary>} path="/courses/:courseId/tasks/:taskId/transcript" />
        <Route element={<RouteErrorBoundary><KeyedWorkQueueDashboard /></RouteErrorBoundary>} path="/queue" />
        <Route element={<RouteErrorBoundary><KeyedMicroLessonReview viewerId={session.userId} /></RouteErrorBoundary>} path="/queue/lesson/:workItemId" />
        <Route element={<RouteErrorBoundary><KeyedPracticeItemReview viewerId={session.userId} /></RouteErrorBoundary>} path="/queue/practice/:workItemId" />
        <Route element={<RouteErrorBoundary><WorkQueueBoard /></RouteErrorBoundary>} path="/board" />
        <Route element={<RouteErrorBoundary><StructuralDiffReview /></RouteErrorBoundary>} path="/queue/structural" />
        <Route element={<RouteErrorBoundary><ReleaseWavesDashboard /></RouteErrorBoundary>} path="/waves" />
        <Route element={<RouteErrorBoundary><ProgrammeDashboard /></RouteErrorBoundary>} path="/programme" />
        <Route element={<RouteErrorBoundary><AuditLogPage /></RouteErrorBoundary>} path="/audit" />
        <Route element={<Navigate replace to="/courses" />} path="*" />
      </Route>
    </Routes>
  );
}

declare global {
  interface Window {
    __mindojoSentryVerification?: { marker: string; sent: boolean };
  }
}

class RouteErrorBoundary extends Component<{ children: ReactNode }, { error: string | null }> {
  override state = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return {
      error: error instanceof Error ? error.message : 'Unexpected route failure',
    };
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800 shadow-sm">
          <p className="font-semibold">This screen failed to render.</p>
          <p className="mt-2">{this.state.error}</p>
          <button
            className="mt-4 rounded-lg border border-rose-300 px-4 py-2 font-semibold"
            onClick={() => {
              this.setState({ error: null });
            }}
            type="button"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
